import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Upload, CheckCircle2, Trash2, FileText } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr } from "../../lib/tenant";

const ACCEPT = ".pdf,.xlsx,.xlsm,.csv,.docx,.pptx,.txt,.jpg,.jpeg,.png,.heic";

export const DocRow = ({ req, files, onUpload, onRemove, busy, optional = false }) => {
  const [pw, setPw] = useState("");
  const needsPw = req.key === "bank_statements";
  return (
    <li className="rounded-xl border border-neutral-200 bg-white p-4" data-testid={`tenant-doc-row-${req.key}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={"flex h-9 w-9 items-center justify-center rounded-lg " + (files.length ? "text-white" : "bg-neutral-100 text-neutral-400")} style={files.length ? { background: "var(--tenant)" } : undefined}>
            {files.length ? <CheckCircle2 size={18} /> : <FileText size={18} />}
          </span>
          <div><p className="text-sm font-bold">{req.label}</p><p className="text-[11px] text-neutral-500">{files.length ? `${files.length} file${files.length > 1 ? "s" : ""} uploaded` : optional ? "Optional" : "Requirement"}</p></div>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-300 px-3 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-50">
          {busy === req.key ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {files.length ? "Add another" : "Upload"}
          <input type="file" multiple accept={ACCEPT} className="hidden" disabled={!!busy} onChange={(e) => { onUpload(req, Array.from(e.target.files || []), pw); e.target.value = ""; }} data-testid={`tenant-doc-input-${req.key}`} />
        </label>
      </div>
      {needsPw && <input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="PDF password (only if your statement is locked)" className="mt-3 w-full rounded-lg border border-neutral-200 px-3 py-1.5 text-xs outline-none" data-testid="tenant-doc-pdf-password" />}
      {files.length > 0 && (
        <ul className="mt-3 space-y-1">
          {files.map((d) => (
            <li key={d.doc_id} className="flex items-center justify-between rounded-lg bg-neutral-50 px-3 py-1.5 text-xs" data-testid={`tenant-doc-file-${d.doc_id}`}>
              <span className="truncate">{d.filename}</span>
              <button type="button" onClick={() => onRemove(d)} className="text-neutral-400 hover:text-red-500" data-testid={`tenant-doc-remove-${d.doc_id}`}><Trash2 size={13} /></button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

export const StepDocuments = ({ documents, onChanged, onBack, onNext }) => {
  const { sub, config } = useTenant();
  const [busy, setBusy] = useState("");
  const reqs = config.required_docs.length ? config.required_docs : [{ key: "bank_statements", label: "6 months bank statements" }, { key: "mgmt_accounts", label: "Latest management accounts" }];
  const filesFor = (req) => documents.filter((d) => req.custom ? d.doc_type === "supporting_docs" && (d.custom_label || "").toLowerCase() === req.label.toLowerCase() : d.doc_type === req.key);
  const allDone = reqs.every((r) => filesFor(r).length > 0);

  const upload = async (req, files, pw) => {
    if (!files.length) return;
    setBusy(req.key);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", req.custom ? "supporting_docs" : req.key);
      if (pw && req.key === "bank_statements") fd.append("pdf_password", pw);
      try {
        const { data } = await tapi.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (req.custom) await tapi.post(`/t/${sub}/documents/${data.doc_id}/label`, { label: req.label });
        toast.success(`${file.name} uploaded`);
      } catch (e) {
        const code = e.response?.data?.detail?.code;
        toast.error(code === "pdf_encrypted" ? "This PDF is password-protected — enter the password below the bank statements row and try again." : apiErr(e, `Couldn't upload ${file.name}`));
      }
    }
    await onChanged(); setBusy("");
  };
  const remove = async (d) => {
    try { await tapi.delete(`/documents/${d.doc_id}`); await onChanged(); } catch (e) { toast.error(apiErr(e, "Couldn't remove file")); }
  };

  return (
    <div className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="tenant-step-documents">
      <div><h2 className="text-lg font-extrabold">Upload your documents</h2><p className="text-sm text-neutral-500">{config.name} needs the following to assess your application. PDF, Excel, Word or a clear photo all work.</p></div>
      <ul className="space-y-3">{reqs.map((r) => <DocRow key={r.key} req={r} files={filesFor(r)} onUpload={upload} onRemove={remove} busy={busy} />)}</ul>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-800" data-testid="docs-back"><ArrowLeft size={14} /> Back</button>
        <div className="flex items-center gap-3">
          {!allDone && <span className="text-[11px] text-neutral-400">You can continue, but missing documents will count against you.</span>}
          <BrandButton onClick={onNext} disabled={!documents.length || !!busy} data-testid="docs-next">Continue to scoring <ArrowRight size={16} /></BrandButton>
        </div>
      </div>
    </div>
  );
};
